import { APP_INITIALIZER, NgModule, provideZoneChangeDetection, signal } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient } from '@angular/common/http';

import { provideTaiga, tuiCalendarOptionsProvider, tuiDateFormatProvider, TuiRoot } from '@taiga-ui/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { SharedModule } from './all-modules/shared/shared.module';
import { CoreModule } from './all-modules/core/core.module';

import { HeaderComponent } from './layout/header/header.component';
import { PageLoaderComponent } from './layout/page-loader/page-loader.component';
import { SidebarComponent } from './layout/sidebar/sidebar.component';
import { RightSidebarComponent } from './layout/right-sidebar/right-sidebar.component';
import { AuthLayoutComponent } from './layout/app-layout/auth-layout/auth-layout.component';
import { MainLayoutComponent } from './layout/app-layout/main-layout/main-layout.component';
import { FooterComponent } from './layout/footer/footer.component';
import { UserProfileComponent } from './layout/user-profile/user-profile.component';

import { SoClientFeedbackComponent } from './expose-compoment/so-client-feedback/so-client-feedback.component';
import { LaborAvailabilityConfirmationComponent } from './expose-compoment/labor-availability-confirmation/labor-availability-confirmation.component';

import { CalenderViewComponent } from './all-modules/job-labor-planning/component/calender-view/calender-view.component';
import { ServiceOrderViewPdfComponent } from './all-modules/service-order/components/service-order-view-pdf/service-order-view-pdf.component';

import { AuthService } from './all-modules/core/service/auth.service';
import { CommonService } from './all-modules/shared/services/common.service';
import { UserManagementService } from './all-modules/admin/services/user-management/user-management.service';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TuiDayOfWeek } from '@taiga-ui/cdk';
import { SecurePhotoGalleryComponent } from './expose-compoment/secure-photo-gallery/secure-photo-gallery.component';
import { ShortLinkRedirectComponent } from './expose-compoment/short-link-redirect/short-link-redirect.component';
import { ReportLinkComponent } from './expose-compoment/report-link/report-link.component';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

export function preloadSession(
  authService: AuthService,
  userService: UserManagementService,
  commonService: CommonService
): () => Promise<void> {
  return () =>
    new Promise<void>((resolve) => {
      const token = localStorage.getItem('token');

      if (!token) {
        resolve();
        return;
      }

      userService.GetLoggedInUserDetails().subscribe({
        next: (res) => {
          commonService.setLoggedUserDetails(res);
          resolve();
        },
        error: () => {
          console.warn('APP_INITIALIZER: Failed to preload session.');
          resolve();
        },
      });
    });
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    PageLoaderComponent,
    SidebarComponent,
    UserProfileComponent,
    SoClientFeedbackComponent,
    SecurePhotoGalleryComponent,
    ShortLinkRedirectComponent,
    ReportLinkComponent,
    RightSidebarComponent,
    AuthLayoutComponent,
    MainLayoutComponent,
    FooterComponent,
    LaborAvailabilityConfirmationComponent,
    CalenderViewComponent,
    ServiceOrderViewPdfComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    TuiRoot,
    AppRoutingModule,
    CoreModule,
    SharedModule,
  ],
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),   // ← restores zone.js-based automatic CD
    provideTaiga(),
    {
      provide: APP_INITIALIZER,
      useFactory: preloadSession,
      deps: [AuthService, UserManagementService, CommonService],
      multi: true,
    },
    tuiDateFormatProvider({
      mode: 'yyyy/mm/dd',
      separator: '-',
    }),
    tuiCalendarOptionsProvider({
      weekStart: signal(TuiDayOfWeek.Sunday),
    })
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
